import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { CheckCircleTwoTone, CloseCircleTwoTone, InfoCircleTwoTone } from '@ant-design/icons';
import { Card, Empty, notification, Popover } from 'antd';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useRunningPnlMutation } from '../../slices/zerodhaApiSlice';
import { setRunningpnl } from '../../slices/runpnlSlice';

const AreaGredientChart = (props) => {
  moment.tz.setDefault("Asia/Calcutta|Asia/Kolkata");
  const { date } = props;
  const dailynetcumulativepnlinfo = (
    <div>
      Daily Net Cumulative P&L data (calculated) Example:
      <ul>
        <li>2024-09-01: $1500</li>
        <li>2024-09-02: $1000 (1500 - 500)</li>
        <li>2024-09-03: $3500 (1000 + 2500)</li>
        <li>2024-09-04: $2500 (3500 - 1000)</li>
        <li>2024-09-05: $3300 (2500 + 800)</li>
      </ul>
    </div>
  );

  const [api, contextHolder] = notification.useNotification();
  const dispatch = useDispatch();
  const { runpnl } = useSelector((state) => state.runpnl);
  const { darkmode } = useSelector((state) => state.darkmode);
  const [data, setData] = useState([]);

  const [runningPnl, { isLoading }] = useRunningPnlMutation();

  const getRunningPnl = async (startDate, endDate) => {
    try {
      const res = await runningPnl({ startDate, endDate }).unwrap();
      console.log(res,"response");
      
      dispatch(setRunningpnl({ ...res }));
    } catch (err) {
      console.log(err);
      api.error({
        message: err?.data?.message || err.error || err?.data?.error,
      });
    }
  };

  const gradientOffset = (pnlData) => {
    if (!pnlData || pnlData.length === 0) return 0.5; // Default to middle

    const dataMax = Math.max(...pnlData.map((i) => i.pnl));
    const dataMin = Math.min(...pnlData.map((i) => i.pnl));

    if (dataMax <= 0) return 0;
    if (dataMin >= 0) return 1;

    return dataMax / (dataMax - dataMin);
  };

  useEffect(() => {
    getRunningPnl(date.startDate, date.endDate);
  }, [date]);

  useEffect(() => {
      setData(runpnl); // Update local data state
  }, [runpnl]);

  const offset = gradientOffset(data);

  const CustomAntTooltip = ({ payload, label }) => {
    if (payload && payload.length) {
      const { pnl } = payload[0].payload;
      return (
        <div className="background-blur" style={{ padding: '10px', width: '150px', textAlign: 'left', color: "#000"}}>
          <p style={{ fontSize: '12px' }}>
            {pnl >= 0 ? (
              <>
                <CheckCircleTwoTone twoToneColor="#2ecc71" /> Profit
              </>
            ) : (
              <>
                <CloseCircleTwoTone twoToneColor="#e74c3c" /> Loss
              </>
            )}
          </p>
          <p style={{ fontSize: '12px' }}>Date: {moment(new Date(label)).format('YYYY/MM/DD')}</p>
          <p style={{ fontSize: '12px' }}>Realised P&L: ₹ {Number(pnl).toFixed(2)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      title="Daily Net Cumulative P&L"
      loading={isLoading}
      extra={
        <Popover content={dailynetcumulativepnlinfo} placement="left" title="Daily Net Cumulative P&L">
          <InfoCircleTwoTone style={{ marginRight: '20px' }} />
        </Popover>
      }
      bordered={false}
      className="card_hover"
    >
      {contextHolder}
      <div style={{ height: 250, textAlign: 'center', marginBottom: '20px' }}>
        {data != null && data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              width={500}
              height={400}
              data={data}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0,
              }}
            >
              <XAxis
                dataKey="date"
                tickFormatter={(value) => `${moment(new Date(value)).format('YYYY/MM/DD')}`}
                style={{ fontSize: '10px' }}
              />
              <YAxis domain={['auto', 'auto']} tickFormatter={(value) => `₹ ${Number(value).toFixed(2)}`} style={{ fontSize: '10px' }} />
              <Tooltip content={<CustomAntTooltip />} />
              <defs>
                <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2ecc71" stopOpacity={1} />
                  <stop offset={offset} stopColor="#2ecc71" stopOpacity={0.3} />
                  <stop offset={offset} stopColor="#e74c3c" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#e74c3c" stopOpacity={1} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="pnl"
                stroke="#2980b9"
                fill="url(#splitColor)"
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%" style={{ alignContent: 'center' }}>
            <Empty />
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default AreaGredientChart;
